/**
 * Modal de confirmación reutilizable.
 *
 * API:
 *   const ok = await openConfirmModal({
 *     title: 'Borrar reporte',
 *     message: '¿Seguro que quieres borrar MR-2026-001?',
 *     confirmLabel: 'Borrar',
 *     danger: true,
 *   });
 *   if (ok) await doDelete();
 *
 * Implementación: un único `<dialog>` nativo creado perezosamente y
 * reusado entre llamadas. El foco va al botón Cancelar (más seguro).
 * Escape, click fuera, o botón Cancelar resuelven `false`. El botón
 * Confirmar resuelve `true`.
 */

import './confirm-modal.css';

export interface ConfirmModalOptions {
  /** Título corto, en mayúsculas a la cabecera. */
  title: string;
  /** Mensaje principal. Texto plano (sin HTML). */
  message: string;
  /** Etiqueta del botón confirmar. Default 'Aceptar'. */
  confirmLabel?: string;
  /** Etiqueta del botón cancelar. Default 'Cancelar'. */
  cancelLabel?: string;
  /** Si true, el confirmar lleva estilo destructivo (rojo). */
  danger?: boolean;
  /**
   * Si true, oculta el botón Cancelar — modal informativo con un único
   * botón (confirmLabel suele ser "Cerrar"). Resolver siempre `true`.
   */
  infoOnly?: boolean;
}

interface ModalRefs {
  dialog: HTMLDialogElement;
  title: HTMLElement;
  message: HTMLElement;
  confirmBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
}

let refs: ModalRefs | null = null;

export function openConfirmModal(options: ConfirmModalOptions): Promise<boolean> {
  const r = ensureModal();

  r.title.textContent = options.title;
  r.message.textContent = options.message;
  r.confirmBtn.textContent = options.confirmLabel ?? 'Aceptar';
  r.cancelBtn.textContent = options.cancelLabel ?? 'Cancelar';
  r.confirmBtn.classList.toggle('cm-confirm--danger', Boolean(options.danger));
  // Modal informativo: solo botón confirm (con texto "Cerrar" típico).
  if (options.infoOnly) {
    r.cancelBtn.setAttribute('hidden', '');
  } else {
    r.cancelBtn.removeAttribute('hidden');
  }

  return new Promise<boolean>((resolve) => {
    let resolved = false;
    const finish = (value: boolean): void => {
      if (resolved) return;
      resolved = true;
      r.dialog.close();
      r.confirmBtn.removeEventListener('click', onConfirm);
      r.cancelBtn.removeEventListener('click', onCancel);
      r.dialog.removeEventListener('cancel', onCancel);
      r.dialog.removeEventListener('click', onBackdropClick);
      resolve(value);
    };
    const onConfirm = (): void => finish(true);
    const onCancel = (ev?: Event): void => {
      ev?.preventDefault();
      finish(false);
    };
    const onBackdropClick = (ev: MouseEvent): void => {
      // Click sobre el propio <dialog> (fuera del contenido) — el
      // contenido vive en `.cm-card`, así que un click directo en el
      // dialog significa backdrop.
      if (ev.target === r.dialog) finish(false);
    };

    r.confirmBtn.addEventListener('click', onConfirm);
    r.cancelBtn.addEventListener('click', onCancel);
    r.dialog.addEventListener('cancel', onCancel);
    r.dialog.addEventListener('click', onBackdropClick);

    r.dialog.showModal();
    // Foco en confirm si el cancelar está oculto; si no, en cancelar
    // (más seguro frente a confirmaciones destructivas accidentales).
    if (options.infoOnly) r.confirmBtn.focus();
    else r.cancelBtn.focus();
  });
}

function ensureModal(): ModalRefs {
  if (refs !== null) return refs;
  const dialog = document.createElement('dialog');
  dialog.className = 'cm-dialog';
  dialog.innerHTML = `
    <div class="cm-card">
      <h2 class="cm-title"></h2>
      <p class="cm-message"></p>
      <div class="cm-actions">
        <button type="button" class="cm-btn cm-cancel"></button>
        <button type="button" class="cm-btn cm-confirm"></button>
      </div>
    </div>
  `;
  document.body.appendChild(dialog);

  const title = dialog.querySelector<HTMLElement>('.cm-title');
  const message = dialog.querySelector<HTMLElement>('.cm-message');
  const confirmBtn = dialog.querySelector<HTMLButtonElement>('.cm-confirm');
  const cancelBtn = dialog.querySelector<HTMLButtonElement>('.cm-cancel');
  if (!title || !message || !confirmBtn || !cancelBtn) {
    throw new Error('[confirm-modal] estructura del dialog rota');
  }
  refs = { dialog, title, message, confirmBtn, cancelBtn };
  return refs;
}
